import type {
  AsyncTransactionsFlushedEvent,
  ColumnMenuVisibleChangedEvent,
  ColumnMovedEvent,
  ColumnPinnedEvent,
  ColumnResizedEvent,
  ColumnRowGroupChangedEvent,
  ColumnVisibleEvent,
  ExpandOrCollapseAllEvent,
  GridApi,
  GridOptions,
  NewColumnsLoadedEvent,
  RowClickedEvent,
  RowGroupOpenedEvent,
  StateUpdatedEvent,
} from 'ag-grid-community';

import { useEffect } from 'react';
import type { UseBlotterTableProps } from '.';
import { useMixpanel } from '../../../contexts';
import { MixpanelEvent, MixpanelEventProperty } from '../../../tokens';
import { handleSiblingLeafPrecisionFlush, type AlignmentCache } from '../../AgGrid/bindSiblingLeafPrecisionRefresh';
import { getAllNodesInGroup, isGridApiReady, resetColumnsIfAllHidden, safeGridApi } from '../utils';

export function useBlotterTableEventHandlers<R>(
  {
    columns,
    onRowClicked,
    persistence,
  }: Pick<UseBlotterTableProps<R>, 'columns' | 'persistence'> & {
    onRowClicked?: GridOptions<R>['onRowClicked'];
  },
  api?: GridApi
) {
  const mixpanel = useMixpanel();
  // Column ordering, width, and visibility

  useEffect(() => {
    function handleColumnsChanged(
      params:
        | ColumnVisibleEvent
        | ColumnMovedEvent
        | ColumnResizedEvent
        | ColumnPinnedEvent
        | ColumnRowGroupChangedEvent
    ) {
      if (!isGridApiReady(api)) {
        return;
      }
      if (
        params.type === 'columnVisible' &&
        params.source !== 'api' /* Fired when we programmatically trigger hide/unhide of columns */ &&
        params.source !== 'toolPanelUi' /* Fired when the user clicks one checkbox in the column picker */ &&
        params.source !==
          'columnMenu' /* Fired when the user clicks the "select all" checkbox in the column picker */ &&
        !(
          /* Fired when the user drags a column to remove it */
          (params.source === 'uiColumnMoved' && params.visible === false)
        )
      ) {
        // Prevent running columns changed hook for non-user-events or when resize is not finished
        return;
      }

      // Skip unfinished or non-user-initiated column moves and resizes
      if (
        (params.type === 'columnMoved' &&
          (params.finished === false || (params.source !== 'uiColumnMoved' && params.source !== 'toolPanelUi'))) ||
        (params.type === 'columnResized' && (params.finished === false || params.source !== 'uiColumnResized'))
      ) {
        return;
      }

      if (params.type === 'columnRowGroupChanged' && params.source === 'toolPanelUi') {
        // The user has changed the row grouping by dragging and dropping. We want to track this in mixpanel.
        mixpanel.track(MixpanelEvent.DragAndDropGrouping, {
          [MixpanelEventProperty.Columns]: params.columns?.map(column => column.getColId()),
        });
      }

      if (params.column && params.type === 'columnVisible' && params.source === 'toolPanelUi') {
        // The user has changed column visibility in the tool panel. We want to track this in mixpanel.
        mixpanel.track(params.column.isVisible() ? MixpanelEvent.ShowBlotterColumn : MixpanelEvent.HideBlotterColumn, {
          [MixpanelEventProperty.Column]: params.column.getColId(),
        });
      }
    }

    safeGridApi(api)?.addEventListener('columnVisible', handleColumnsChanged);
    safeGridApi(api)?.addEventListener('columnMoved', handleColumnsChanged);
    safeGridApi(api)?.addEventListener('columnResized', handleColumnsChanged);
    safeGridApi(api)?.addEventListener('columnPinned', handleColumnsChanged);
    safeGridApi(api)?.addEventListener('columnRowGroupChanged', handleColumnsChanged);
    return () => {
      safeGridApi(api)?.removeEventListener('columnVisible', handleColumnsChanged);
      safeGridApi(api)?.removeEventListener('columnMoved', handleColumnsChanged);
      safeGridApi(api)?.removeEventListener('columnResized', handleColumnsChanged);
      safeGridApi(api)?.removeEventListener('columnPinned', handleColumnsChanged);
      safeGridApi(api)?.removeEventListener('columnRowGroupChanged', handleColumnsChanged);
    };
  }, [api, columns, mixpanel, persistence]);

  // For our persistence functionality, we connect them directly via their own event listeners. AgGrid can open several event listeners per event type in this fashion.
  const persistenceEventHandlers = persistence?.eventHandlers;
  useEffect(() => {
    if (!persistenceEventHandlers) {
      return;
    }

    function handleGridStateUpdated(event: StateUpdatedEvent) {
      if (!isGridApiReady(api)) {
        return;
      }

      persistenceEventHandlers?.onStateUpdated?.(event);
    }

    function handleExpandOrCollapseAll(event: ExpandOrCollapseAllEvent) {
      if (!isGridApiReady(api)) {
        return;
      }

      persistenceEventHandlers?.onExpandOrCollapseAll?.(event);
    }

    function handleColumnRowGroupChanged(event: ColumnRowGroupChangedEvent) {
      if (!isGridApiReady(api)) {
        return;
      }

      persistenceEventHandlers?.onColumnRowGroupChanged?.(event);
    }

    function handleRowGroupOpened(event: RowGroupOpenedEvent) {
      if (!isGridApiReady(api)) {
        return;
      }

      persistenceEventHandlers?.onRowGroupOpened?.(event);
    }

    function handleNewColumnsLoaded(event: NewColumnsLoadedEvent) {
      if (!isGridApiReady(api)) {
        return;
      }

      persistenceEventHandlers?.onNewColumnsLoaded?.(event);
    }

    safeGridApi(api)?.addEventListener('stateUpdated', handleGridStateUpdated);
    safeGridApi(api)?.addEventListener('expandOrCollapseAll', handleExpandOrCollapseAll);
    safeGridApi(api)?.addEventListener('columnRowGroupChanged', handleColumnRowGroupChanged);
    safeGridApi(api)?.addEventListener('rowGroupOpened', handleRowGroupOpened);
    safeGridApi(api)?.addEventListener('newColumnsLoaded', handleNewColumnsLoaded);
    return () => {
      safeGridApi(api)?.removeEventListener('stateUpdated', handleGridStateUpdated);
      safeGridApi(api)?.removeEventListener('expandOrCollapseAll', handleExpandOrCollapseAll);
      safeGridApi(api)?.removeEventListener('columnRowGroupChanged', handleColumnRowGroupChanged);
      safeGridApi(api)?.removeEventListener('rowGroupOpened', handleRowGroupOpened);
      safeGridApi(api)?.removeEventListener('newColumnsLoaded', handleNewColumnsLoaded);
    };
  }, [api, persistenceEventHandlers]);

  useEffect(() => {
    function handleRowClicked(params: RowClickedEvent<R>) {
      if (params.data) {
        onRowClicked?.(params);
      }

      let isClickSelectionEnabled = true;
      const rowSelectionOptions = params.api.getGridOption('rowSelection');
      if (typeof rowSelectionOptions !== 'string' && typeof rowSelectionOptions?.enableClickSelection === 'boolean') {
        // [DEAL-5219] If click selection is disabled, we do not want to trigger the internal selection logic below.
        isClickSelectionEnabled = rowSelectionOptions.enableClickSelection;
      }

      // If we are clicking on a group node, we want to (un)select all the children within the group as well.
      if (params.node.group && isClickSelectionEnabled) {
        // The selection state toggling has already happened at this point, so getting this isSelected gives us the new selected state!
        const newSelectedState = params.node.isSelected();

        // There's a strange case where isSelected returns undefined if the group contents are partially selected, but I can't replicate that....
        // For now just return early. Maybe AgGrid's documentation is incorrect? Can only make it return true or false.
        if (newSelectedState === undefined) {
          return;
        }
        // Apply this new selected state to all children within this group recursively
        const nodes = [params.node, ...getAllNodesInGroup(params.node)];
        params.api.setNodesSelected({ nodes, newValue: newSelectedState, source: 'rowClicked' });
      }
    }

    safeGridApi(api)?.addEventListener('rowClicked', handleRowClicked);
    return () => {
      safeGridApi(api)?.removeEventListener('rowClicked', handleRowClicked);
    };
  }, [api, onRowClicked]);

  useEffect(() => {
    function handleColumnMenuVisibleChanged(params: ColumnMenuVisibleChangedEvent) {
      if (!isGridApiReady(params.api)) {
        return;
      }
      // Trigger on Column Menu Closed
      if (!params.visible) {
        resetColumnsIfAllHidden(params);
      }
    }

    safeGridApi(api)?.addEventListener('columnMenuVisibleChanged', handleColumnMenuVisibleChanged);
    return () => {
      safeGridApi(api)?.removeEventListener('columnMenuVisibleChanged', handleColumnMenuVisibleChanged);
    };
  }, [api]);

  const needsSiblingPrecisionFlush = columns.some(
    col => !('children' in col) && col.params?.siblingLeafPrecision != null
  );

  useEffect(() => {
    if (!needsSiblingPrecisionFlush) {
      return;
    }
    const gridApi = safeGridApi(api);
    if (gridApi == null) {
      return;
    }
    const alignmentCache: AlignmentCache = new WeakMap();
    const handleAsyncTransactionsFlushed = (event: AsyncTransactionsFlushedEvent) => {
      handleSiblingLeafPrecisionFlush(gridApi, event, alignmentCache);
    };
    gridApi.addEventListener('asyncTransactionsFlushed', handleAsyncTransactionsFlushed);
    return () => {
      safeGridApi(api)?.removeEventListener('asyncTransactionsFlushed', handleAsyncTransactionsFlushed);
    };
  }, [api, needsSiblingPrecisionFlush]);
}
